import React, { useState, useEffect, useCallback, useMemo } from 'react';
import EditarCadastrarHorario from './EditarCadastrar';
import { Container, InputGroup } from 'rsuite';
import { useAuth } from '../../hooks/auth';
import { ContentHeader } from '../../components/ContentHeader';
import DuvidasButton from '../../components/DuvidasButton';
import RModal from '../../components/Rsuite/Modal';
import PlusIcon from '@rsuite/icons/Plus';
import SearchIcon from '@rsuite/icons/Search';
import { CustomInput } from '../../components/CustomInput';
import { CustomButton } from '../../components/CustomButton';
import api from '../../services/api';
import { debounce } from '../../components/funcoes';
import { useCustomTheme } from '../../hooks/useCustomTheme';
import { toastInfo, toastError } from '../../toasts';
import {
  FaClock,
  FaEllipsisV,
  FaInfoCircle,
  FaExclamationTriangle,
} from 'react-icons/fa';
import {
  CardGrid,
  Card,
  CardHeader,
  CardTitle,
  CornerTag,
  StatusBadge,
  ItemList,
  ItemRow,
  ItemText,
  AddButton,
  Hint,
  LoadingState,
  CardMenu,
} from '../../components/ManagementCardGrid';
import { diasDaSemana, diasDaSemanaOrdenadas } from './constants';
import {
  ConflictBanner,
  ConflictIcon,
  ConflictContent,
  ConflictTitle,
  ConflictSubtitle,
  ConflictList,
  ConflictChip,
} from './styles';

const formatarHora = (hora = '') =>
  hora?.length > 5 ? hora.slice(0, 5) : hora;

export default function Horarios() {
  const { id_empresa } = useAuth();
  const [horarios, setHorarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [horario, setHorario] = useState(null);
  const [diaFixo, setDiaFixo] = useState(null);
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [horarioParaExcluir, setHorarioParaExcluir] = useState(null);
  const { theme } = useCustomTheme();

  const horariosMemo = useMemo(
    () =>
      busca.trim() === ''
        ? horarios
        : horarios.filter(({ DiaSemana = '' }) =>
            DiaSemana?.toLocaleLowerCase().includes(busca),
          ),
    [busca, horarios],
  );

  const horariosPorDia = useMemo(() => {
    const mapa = {};
    diasDaSemanaOrdenadas.forEach((dia) => {
      mapa[dia.value] = [];
    });
    horariosMemo.forEach((item) => {
      if (!mapa[item.DiaSemana]) mapa[item.DiaSemana] = [];
      mapa[item.DiaSemana].push(item);
    });
    Object.values(mapa).forEach((lista) =>
      lista.sort((a, b) => (a.Inicio || '').localeCompare(b.Inicio || '')),
    );
    return mapa;
  }, [horariosMemo]);

  const conflitos = useMemo(() => {
    const porDia = {};
    horarios.forEach((item) => {
      if (!porDia[item.DiaSemana]) porDia[item.DiaSemana] = [];
      porDia[item.DiaSemana].push(item);
    });

    const pares = [];
    const idsConflitantes = new Set();

    Object.values(porDia).forEach((lista) => {
      for (let i = 0; i < lista.length; i++) {
        for (let j = i + 1; j < lista.length; j++) {
          const a = lista[i];
          const b = lista[j];
          if (a.Inicio < b.Fim && b.Inicio < a.Fim) {
            pares.push({ dia: a.DiaSemana, a, b });
            idsConflitantes.add(a.Id);
            idsConflitantes.add(b.Id);
          }
        }
      }
    });

    return { pares, idsConflitantes };
  }, [horarios]);

  const atualizar = async () => {
    await api
      .get(`horario/listarGestor/${id_empresa}`)
      .then((res) => {
        setHorarios(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const abrirDados = (horario) => {
    setHorario(horario);
    setDiaFixo(null);
    setOpen(true);
  };

  const cadastrar = useCallback(() => {
    setHorario(null);
    setDiaFixo(null);
    setOpen(true);
  }, []);

  const cadastrarParaDia = useCallback((dia) => {
    setHorario(null);
    setDiaFixo(dia.value);
    setOpen(true);
  }, []);

  const alterarStatus = async (hora, status) => {
    await api
      .patch(`horario/alterar/${hora.Id}`, {
        Inicio: hora.Inicio,
        Fim: hora.Fim,
        Ativo: status,
      })
      .then((res) => {
        toastInfo('Horário alterado');
        setHorarios((state) =>
          state.map((e) => (e.Id !== res.data.Id ? e : res.data)),
        );
      })
      .catch((error) =>
        toastError(error?.response?.data?.message || 'Erro ao alterar horário'),
      );
  };

  const excluirHorario = async () => {
    const id = horarioParaExcluir?.Id;
    if (!id) return;

    await api
      .delete(`horario/excluir/${id}`)
      .then(() => {
        toastInfo('Horário excluído com sucesso');
        setHorarios((state) => state.filter((e) => e.Id !== id));
        setHorarioParaExcluir(null);
      })
      .catch((error) =>
        toastError(error?.response?.data?.message || 'Erro ao excluir horário'),
      );
  };

  const debounceSearch = debounce((valor = '') => {
    setBusca(valor.toLocaleLowerCase());
  }, 1000);

  useEffect(() => {
    atualizar();
  }, []);

  const hojeJsDay = new Date().getDay();

  return (
    <Container
      style={{
        padding: '10px 28px',
        height: 'calc(100vh - 60px)',
        backgroundColor: theme.colors.background,
        overflowY: 'auto',
        maxWidth: 1600,
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      <ContentHeader
        title="Horários"
        subtitle="Gerencie os horários de funcionamento da sua empresa"
        icon={FaClock}
        secondComponent={
          <InputGroup
            inside
            style={{
              width: '36vw',
              background: theme.colors.background,
              border: 'none',
            }}
          >
            <CustomInput onChange={debounceSearch} />
            <InputGroup.Addon>
              <SearchIcon color={theme.colors.textSecondary} />
            </InputGroup.Addon>
          </InputGroup>
        }
        thirdComponent={
          <>
            <DuvidasButton tela="horario" />
            <CustomButton appearance="primary" onClick={() => cadastrar()}>
              <PlusIcon style={{ marginRight: 6 }} /> Adicionar Horário
            </CustomButton>
          </>
        }
      />

      {conflitos.pares.length > 0 && (
        <ConflictBanner>
          <ConflictIcon>
            <FaExclamationTriangle />
          </ConflictIcon>
          <ConflictContent>
            <ConflictTitle>
              Você tem horários que estão coincidindo, arrume-os
            </ConflictTitle>
            <ConflictSubtitle>
              Os horários abaixo se sobrepõem no mesmo dia. Edite ou exclua um
              deles para evitar erros no funcionamento da loja.
            </ConflictSubtitle>
            <ConflictList>
              {conflitos.pares.map((par, index) => {
                const diaInfo = diasDaSemanaOrdenadas.find(
                  (d) => d.value === par.dia,
                );
                return (
                  <ConflictChip key={index}>
                    <strong>{diaInfo?.label || par.dia}</strong>
                    <b>
                      {formatarHora(par.a.Inicio)}-{formatarHora(par.a.Fim)}
                    </b>
                    ×
                    <b>
                      {formatarHora(par.b.Inicio)}-{formatarHora(par.b.Fim)}
                    </b>
                  </ConflictChip>
                );
              })}
            </ConflictList>
          </ConflictContent>
        </ConflictBanner>
      )}

      {loading ? (
        <LoadingState>Carregando...</LoadingState>
      ) : (
        <CardGrid columns={7}>
          {diasDaSemanaOrdenadas.map((dia) => {
            const slots = horariosPorDia[dia.value] || [];
            const temAtivo = slots.some((s) => s.Ativo);

            return (
              <Card
                key={dia.value}
                $selected={diaSelecionado === dia.value}
                onClick={() => setDiaSelecionado(dia.value)}
              >
                {dia.jsDay === hojeJsDay && <CornerTag>Hoje</CornerTag>}
                <CardHeader>
                  <CardTitle>{dia.label}</CardTitle>

                  {slots.length > 0 && (
                    <StatusBadge
                      $active={temAtivo}
                      onClick={() => {
                        const novoStatus = temAtivo ? 0 : 1;
                        slots.forEach((s) => alterarStatus(s, novoStatus));
                      }}
                    >
                      {temAtivo ? 'Ativo' : 'Inativo'}
                    </StatusBadge>
                  )}
                </CardHeader>

                <ItemList>
                  {slots.map((slot) => {
                    const emConflito = conflitos.idsConflitantes.has(slot.Id);
                    return (
                      <ItemRow key={slot.Id}>
                        <ItemText
                          $muted={!slot.Ativo}
                          $strike={!slot.Ativo}
                          style={
                            emConflito
                              ? { color: theme.colors.danger }
                              : undefined
                          }
                        >
                          {emConflito ? (
                            <FaExclamationTriangle
                              size={11}
                              title="Conflita com outro horário"
                            />
                          ) : (
                            <FaClock size={11} />
                          )}
                          {formatarHora(slot.Inicio)} - {formatarHora(slot.Fim)}
                        </ItemText>
                        <CardMenu
                          options={[
                            {
                              label: 'Editar',
                              onClick: () => abrirDados(slot),
                            },
                            {
                              label: slot.Ativo ? 'Desativar' : 'Ativar',
                              onClick: () =>
                                alterarStatus(slot, slot.Ativo ? 0 : 1),
                            },
                            {
                              label: 'Excluir',
                              danger: true,
                              onClick: () => setHorarioParaExcluir(slot),
                            },
                          ]}
                        />
                      </ItemRow>
                    );
                  })}
                </ItemList>

                <AddButton
                  onClick={(e) => {
                    e.stopPropagation();
                    const diaCompleto = diasDaSemana.find(
                      (d) => d.value.value === dia.value,
                    );
                    cadastrarParaDia(diaCompleto);
                  }}
                >
                  <PlusIcon /> Adicionar horário
                </AddButton>
              </Card>
            );
          })}
        </CardGrid>
      )}

      <Hint style={{ marginTop: 'auto' }}>
        <FaInfoCircle />
        <span>
          Clique em "Adicionar horário" para cadastrar um turno no dia desejado,
          ou no <FaEllipsisV size={10} style={{ margin: '0 2px' }} /> de um
          horário existente para editá-lo ou ativar/desativar.
        </span>
      </Hint>

      {open && (
        <EditarCadastrarHorario
          modal={open}
          close={() => setOpen(false)}
          horario={horario}
          diaFixo={diaFixo}
          setHorarios={setHorarios}
          id_empresa={id_empresa}
        />
      )}

      {horarioParaExcluir && (
        <RModal
          open={!!horarioParaExcluir}
          onClose={() => setHorarioParaExcluir(null)}
          title="Excluir Horário"
          footer={
            <>
              <CustomButton
                onClick={excluirHorario}
                appearance="primary"
                color="red"
              >
                Excluir
              </CustomButton>
              <CustomButton
                onClick={() => setHorarioParaExcluir(null)}
                appearance="ghost"
              >
                Cancelar
              </CustomButton>
            </>
          }
        >
          Tem certeza que deseja excluir o horário{' '}
          <strong>
            {formatarHora(horarioParaExcluir.Inicio)} -{' '}
            {formatarHora(horarioParaExcluir.Fim)}
          </strong>
          ? Essa ação não pode ser desfeita.
        </RModal>
      )}
    </Container>
  );
}
